import { useMemo, type ReactNode } from "react";
import {
  Badge,
  Box,
  Card,
  Icon,
  ScrollArea,
  Separator,
  Sheet,
  SheetPopup,
  SheetTitle,
  Text,
  TileIconSquare,
  useWidgetRegistry,
  type WidgetInstance,
} from "@salestrak/ui";
import type { ManagedWidget } from "#app/widgets/dashboards/managed-widgets";
import {
  ANCHORED_SHEET_POPUP_CLASS,
  useContentAnchorInsets,
} from "#app/lib/hooks/use-anchor-insets";
import type { Omittable } from "@salestrak/ui";

export type ManageWidgetsPanelProps = Omittable<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The fixed catalog of widgets this surface offers. */
  catalog: ManagedWidget[];
  /** The placed instances — a widget is "on" when an instance carries its `widgetKey`. */
  instances: WidgetInstance[];
  /** Toggle a catalog widget on (add at its designed slot) or off (remove it). */
  onToggle: (widget: ManagedWidget, enabled: boolean) => void;
  /** Optional surface-settings section (My Work's page-chrome settings),
   *  at the top above the widget toggles — ONE panel for everything the
   *  gear configures. */
  settings?: ReactNode;
}>;

/** The panel's 22rem sidebar width, as a class. */
const PANEL_POPUP = "w-88 max-w-none";

/** A subtle hover border cue on top of the stock card. Radius comes from the
 *  Card `radius` prop so the inner highlight ring stays in lockstep.
 *  DESIGN-TUNABLE. */
const CATALOG_TILE_CLASS =
  "cursor-pointer text-left transition-colors hover:border-(--border-strong)";

/** The stable key an instance carries linking it back to its catalog entry. */
function instanceWidgetKey(instance: WidgetInstance): string | undefined {
  return (instance.config as { widgetKey?: string } | null)?.widgetKey ?? undefined;
}

/**
 * Manage widgets — a managed surface's ONLY authoring control. My Work and Team
 * Overview are curated sets (not a free add-any-widget gallery): this right
 * side panel lists that catalog as tiles with a gradient icon square top-left,
 * Added/Add pill top-right, label, and description while the board stays
 * visible behind it. The board reflows under GRAVITY around every toggle:
 * toggling on re-enters the widget at its designed slot (settled through the
 * grid so nothing overlaps); toggling off removes it and the survivors float
 * up to close the hole — the board always reads as a settled layout.
 *
 * Non-modal by construction (`modal={false}` + no backdrop): the old
 * `nonBlockingOverlay` prop bought exactly this, and Base UI leaves the page
 * scrollable and interactive for free.
 */
export function ManageWidgetsPanel({
  open,
  onOpenChange,
  catalog,
  instances,
  onToggle,
  settings,
}: ManageWidgetsPanelProps) {
  const registry = useWidgetRegistry();
  // The ONE side-panel presentation: inset within the content card exactly
  // like the issue-view sidebar, full content height on every surface.
  const insets = useContentAnchorInsets(open);
  // Which catalog entries are currently placed, matched by the baked `widgetKey`.
  const enabledKeys = useMemo(
    () => new Set(instances.map(instanceWidgetKey).filter((k): k is string => !!k)),
    [instances],
  );
  const enabledCount = useMemo(
    () => catalog.filter((widget) => enabledKeys.has(widget.widgetKey)).length,
    [catalog, enabledKeys],
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetPopup
        className={`${PANEL_POPUP} ${ANCHORED_SHEET_POPUP_CLASS}`}
        showBackdrop={false}
        style={{
          marginBottom: insets.bottom,
          marginRight: insets.right,
          marginTop: insets.top,
        }}
      >
        <Box paddingX="md" paddingY="sm" direction="row" align="center" gap="sm">
          <SheetTitle className="text-base">Manage widgets</SheetTitle>
          <Text size="xs" variant="muted">
            {`${enabledCount} of ${catalog.length} shown`}
          </Text>
        </Box>
        <Box paddingX="md" paddingBottom="sm">
          <Text size="xs" variant="muted">
            Toggle widgets on or off — the board updates live and settles around your changes.
          </Text>
        </Box>

        {settings ? (
          <>
            <Box paddingX="md" paddingY="md">
              {settings}
            </Box>
            <Separator />
          </>
        ) : null}

        <ScrollArea>
          <Box direction="column" gap="sm" paddingX="md" paddingY="md">
            {catalog.map((widget) => {
              const enabled = enabledKeys.has(widget.widgetKey);
              return (
                <Card
                  key={widget.widgetKey}
                  render={<Box as="button" />}
                  className={CATALOG_TILE_CLASS}
                  radius="lg"
                  onClick={() => onToggle(widget, !enabled)}
                  aria-label={`${enabled ? "Hide" : "Show"} ${widget.label}`}
                >
                  <Box direction="column" gap="sm" paddingX="md" paddingY="md">
                    <Box direction="row" align="center" justify="between">
                      <Box pointerEvents="none">
                        <TileIconSquare
                          icon={registry.get(widget.type)?.icon ?? "square"}
                          color={enabled ? widget.color : undefined}
                        />
                      </Box>
                      <Badge
                        variant={enabled ? "secondary" : "outline"}
                        className="gap-1 rounded-full px-2"
                      >
                        <Icon name={enabled ? "check" : "plus"} size="xs" />
                        {enabled ? "Added" : "Add"}
                      </Badge>
                    </Box>
                    <Box direction="column" gap="2xs">
                      <Text size="sm" weight="semibold" truncate>
                        {widget.label}
                      </Text>
                      <Text size="xs" variant="muted" truncate={2}>
                        {widget.description}
                      </Text>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
        </ScrollArea>
      </SheetPopup>
    </Sheet>
  );
}
